<template>
  <div
    class="justify-center w-full h-full d-flex align-center login-hero"
    style="background: white"
  >
    <r-img
      alt="Company Logo"
      :class="{
        'login-logo': $vuetify.display.mdAndUp,
        'login-logo-mobile': $vuetify.display.smAndDown,
      }"
      :src="logo"
      width="140"
      :style="{ zIndex: 100 }"
      cover
    />

    <div
      v-if="company && activeStep"
      style="z-index: 2"
      class="h-full d-flex flex-column justify-space-between align-center registration-container"
    >
      <v-stepper
        v-if="isSelfOnboarding"
        v-model="step"
        alt-labels
        :class="[
          'h-full mt-16 stepper d-flex flex-column align-center mt-md-0',
          isBillwerkActive
            ? 'justify-space-between'
            : !$vuetify.display.mdAndDown
              ? 'justify-space-evenly'
              : '',
        ]"
      >
        <!-- Updated stepper header -->
        <v-stepper-header
          class="flex-nowrap flat"
          style="width: 400px"
          alt-labels
        >
          <template
            v-for="({ title }, index) in steps"
            :key="`step-${title}-${index}`"
          >
            <v-stepper-item
              :value="index + 1"
              :complete="step > index + 1"
              class="stepper-head"
              color="primary"
            >
              <template v-slot:icon="{ props }">
                <v-icon
                  v-if="step > index + 1"
                  icon="mdi-check"
                  v-bind="props"
                />
                <div v-else>
                  <div class="text-center">
                    {{ index + 1 }}
                  </div>
                </div>
              </template>
              <div class="text-center">
                {{ title }}
              </div>
            </v-stepper-item>
            <v-divider v-if="index !== steps.length - 1" />
          </template>
        </v-stepper-header>

        <v-stepper-window class="justify-center d-flex stepper-items align-center">
          <v-stepper-window-item
            :value="1"
            class="stepper-content"
          >
            <Website
              v-if="activeStep === 'WEBSITE'"
              :is-billwerk-active="isBillwerkActive"
              :style="{ width: $vuetify.display.smAndDown ? '100%' : '450px' }"
              @completed="submitWebsite"
            />
          </v-stepper-window-item>

          <v-stepper-window-item
            :value="2"
            class="stepper-content align-left"
          >
            <CRMConfig
              v-if="hasFreeSignUp && activeStep === 'CRM_CONFIG'"
              @completed="submitCrmConfig"
            />
            <Billing
              v-if="!hasFreeSignUp && activeStep === 'BILLING'"
              :style="{ width: $vuetify.display.smAndDown ? '100%' : '1300px' }"
              :country-code="countryCode"
              @completed="submitBilling"
            />

            <v-row
              v-if="(hasFreeSignUp || !isBillwerkActive) && activeStep === 'FINISHED'"
              class="px-4 pd-md-0"
              :style="{ width: $vuetify.display.smAndDown ? '100%' : '830px' }"
            >
              <Loading @completed="submitBilling" />
            </v-row>

            <Error
              v-if="activeStep === 'ERROR'"
              style="width: 400px"
            />
          </v-stepper-window-item>
        </v-stepper-window>

        <!-- AGB and Legal -->
        <div
          v-if="isBillwerkActive"
          class="justify-center mt-8 mb-4 d-flex flex-column flex-md-row align-center"
        >
          <template v-if="useIubenda">
            <IubendaDocumentLink type="privacyPolicy" />
            <IubendaConsentLink class="ml-4" />
            <IubendaDocumentLink
              type="terms"
              class="mx-4"
            />
          </template>
          <template v-else>
            <a
              :href="publicDataPrivacyUrl"
              target="_blank"
              class="my-2 my-md-0"
            >
              {{ $t('legal.topic.privacy.title') }}
            </a>
            <span class="mx-2 d-none d-md-block">|</span>
            <Feature
              v-slot="{ feature }"
              :feature-slug="featureNames.DATA_PRIVACY"
            >
              <div
                v-if="feature.config.hasConditions"
                class="d-flex flex-column flex-md-row"
              >
                <a
                  :href="feature.config.conditionsUrl"
                  target="_blank"
                  class="my-2 my-md-0"
                >
                  {{ $t('registration.links.license-agreement') }}
                </a>
                <span class="mx-2 d-none d-md-block">|</span>
              </div>
            </Feature>
          </template>
          <a
            :href="imprintUrl"
            target="_blank"
            class="my-2 my-md-0"
          >
            {{ $t('registration.links.imprint') }}
          </a>
        </div>
      </v-stepper>

      <div
        v-else
        style="width: 300px"
        class="justify-center d-flex flex-column"
      >
        <v-alert
          class="mt-4"
          icon="mdi-check"
          variant="outlined"
        >
          {{ $t('registration.alerts.completed') }}
        </v-alert>
        <v-btn
          to="/dashboard"
          color="primary"
        >
          {{ $t('registration.buttons.continue-to-app') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import logo from '@/assets/img/logo.png'
import { iubenda } from '@/lib/features'
import brandingMixin from '@/mixins/branding'
import featureMixin from '@/mixins/feature'
import IubendaConsentLink from '@/modules/iubenda/ConsentPreferencesLink.vue'
import IubendaDocumentLink from '@/modules/iubenda/DocumentLink.vue'
import COMPANY_BASE from '@/queries/CompanyBase.gql'

import Billing from './Billing.vue'
import CRMConfig from './CRMConfig.vue'
import Error from './Error.vue'
import Loading from './Loading.vue'
import GET_CURRENT_ONBOARDING_STEP from './queries/GetCurrentOnboardingStep.gql'
import Website from './Website.vue'

const stepperToStep = {
  WEBSITE: 1,
  CRM_CONFIG: 2,
  BILLING: 2,
  FINISHED: 2,
}
export default {
  components: {
    Website,
    CRMConfig,
    Loading,
    Billing,
    Error,
    IubendaDocumentLink,
    IubendaConsentLink,
  },
  mixins: [featureMixin, brandingMixin],
  provide() {
    return {
      billingOnboardingDetailsData: {},
    }
  },
  data() {
    return {
      step: 1,
      activeStep: null,
      countryCode: 'de',
      useIubenda: iubenda?.isActive,
      logo,
    }
  },
  computed: {
    // Billwerk is not active for SMG, so we return false to hide the billing step and related features.
    isBillwerkActive() {
      return this.isFeatureActive(this.featureNames.BILLWERK)
    },
    hasFreeSignUp() {
      return this.company?.hasFreeSignUp
    },
    isSelfOnboarding() {
      return this.company?.state?.stringValues?.includes('SELF_ONBOARDING')
    },
    steps() {
      const step2Key = this.isBillwerkActive
        ? this.hasFreeSignUp
          ? 'registration.steps.2-free'
          : 'registration.steps.2-payment'
        : 'registration.steps.2-free'

      return [
        { title: this.$t('registration.steps.1'), step: 1 },
        { title: this.$t(step2Key), step: 2 },
      ]
    },
  },
  watch: {
    activeStep() {
      this.step = stepperToStep[this.activeStep]
    },
  },
  methods: {
    submitWebsite(countryCode) {
      this.countryCode = countryCode
      if (!this.isBillwerkActive) {
        this.step = 2
        this.activeStep = 'FINISHED'
      } else if (!this.hasFreeSignUp) {
        this.fetchCurrentOnboardingStep()
      } else {
        this.activeStep = 'CRM_CONFIG'
      }
    },
    async submitBilling() {
      this.$router.push('/dashboard')
    },
    async fetchCurrentOnboardingStep() {
      this.$apollo.queries.getCurrentOnboardingStep.refetch()
    },
    async submitCrmConfig() {
      this.activeStep = 'FINISHED'
    },
  },
  apollo: {
    company: {
      query: COMPANY_BASE,
      variables() {
        return {
          id: this.$auth.user.companyId,
        }
      },
      skip() {
        return this.$auth.loading || !this.$auth.isAuthenticated || !this.$auth.user.companyId
      },
    },
    getCurrentOnboardingStep: {
      query: GET_CURRENT_ONBOARDING_STEP,
      variables() {
        return {
          id: this.$auth.user.companyId,
        }
      },
      skip() {
        return this.$auth.loading || !this.$auth.isAuthenticated || !this.$auth.user.companyId
      },
      update({ getCurrentOnboardingStep }) {
        this.activeStep = getCurrentOnboardingStep.name
      },
    },
  },
}
</script>

<style scoped>
.login-logo {
  position: absolute;
  top: 0;
  left: 0;
  margin-top: 30px;
  margin-left: 30px;
  z-index: 2;
}

.login-logo-mobile {
  position: absolute;
  top: 0;
  margin-top: 30px;
  left: auto;
  right: auto;
  z-index: 2;
}

.registration-container {
  overflow: scroll;
  overflow-x: hidden;
}

.flat {
  box-shadow: none;
  -webkit-box-shadow: none;
}

.login-hero {
  position: relative;
  height: 100%;
}

.login-logo {
  position: absolute;
  top: 0;
  left: 0;
  margin-top: 30px;
  margin-left: 30px;
  z-index: 2;
}

.stepper-content {
  min-width: 300px;
  max-width: 100%;
  padding: 0px;
  display: flex;
  justify-content: center;
}
</style>

<style>
@media only screen and (max-width: 959.98px) {
  .registration-container > .v-stepper:not(.v-stepper--vertical) .v-stepper__label {
    display: blocK !important;
  }
}
</style>
